import { Button } from '@ui/Button';
import { ReactNode } from 'react';
import { cn } from '@shared/util/classnames';
import { useCallbackOnEnter } from '@shared/util/hooks/useCallbackOnEnter';
import { Modal, ModalContent, ModalProps } from './Modal';

export interface AlertProps extends Omit<ModalProps, 'title' | 'children'> {
  title?: ReactNode;
  message?: ReactNode;
  children?: ReactNode;
  buttonLabel?: string;
  modal?: boolean;
  closeOnEnter?: boolean;
}

export function Alert({ modal = true, ...props }: AlertProps) {
  if (modal) {
    return <ModalAlert {...props} />;
  } else {
    return <AlertContent {...props} />;
  }
}

function ModalAlert({
  title,
  message,
  children,
  buttonLabel,
  size = 'xs',
  closeOnEnter,
  ...modalProps
}: AlertProps) {
  return (
    <Modal {...modalProps} variant="alert" size={size}>
      <ModalContent className="p-6">
        {onClose => (
          <AlertContent
            {...{
              title,
              message,
              children,
              buttonLabel,
              onClose,
              closeOnEnter,
            }}
          />
        )}
      </ModalContent>
    </Modal>
  );
}

function AlertContent({
  title,
  message,
  children = message,
  buttonLabel = 'OK',
  onClose,
  className,
  closeOnEnter = true,
}: AlertProps) {
  useCallbackOnEnter({ callback: onClose, enabled: closeOnEnter });

  return (
    <div className={cn('flex flex-col gap-3 max-w-[18rem] w-fit', className)}>
      {title && <AlertHeader>{title}</AlertHeader>}
      {children && <AlertBody>{children}</AlertBody>}
      <AlertFooter>
        <Button color="primary" onPress={onClose}>
          {buttonLabel}
        </Button>
      </AlertFooter>
    </div>
  );
}

export function AlertHeader({ children }: { children: ReactNode }) {
  return (
    <header className="text-large font-semibold flex flex-row justify-center py-0 text-center px-0 text-balance leading-snug">
      {children}
    </header>
  );
}

export function AlertBody({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center text-center py-0 px-2 text-balance">{children}</div>
  );
}

export function AlertFooter({ children }: { children: ReactNode }) {
  return (
    <footer className="flex flex-row justify-center px-0 py-0 items-center gap-2 w-full">
      {children}
    </footer>
  );
}
